import * as React from 'react'

import Localization from '~/client/src/shared/localization/LocalizationManager'
import ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'

import { formatDuration, formatTickLabel } from './format'
import { MS_PER_DAY, Session, STRIP_TICK_HOURS } from './types'

const StripGridLines: React.FC = React.memo(() => {
  return (
    <>
      {STRIP_TICK_HOURS.filter(h => h > 0).map(h => (
        <div
          key={h}
          aria-hidden
          className="tw:absolute tw:inset-y-0 tw:w-px tw:bg-border/60"
          style={{ left: `${(h / 24) * 100}%` }}
        />
      ))}
    </>
  )
})
StripGridLines.displayName = 'StripGridLines'

const StripTickLabels: React.FC = React.memo(() => {
  return (
    <div className="tw:relative tw:h-3">
      {STRIP_TICK_HOURS.map(h => (
        <span
          key={h}
          aria-hidden
          className="tw:absolute tw:top-0 tw:text-label-sm tw:text-muted-foreground tw:tabular-nums"
          style={{
            left: `${(h / 24) * 100}%`,
            transform: h === 0 ? 'translateX(0)' : 'translateX(-50%)',
          }}
        >
          {formatTickLabel(h)}
        </span>
      ))}
      <span
        aria-hidden
        className="tw:absolute tw:right-0 tw:top-0 tw:text-label-sm tw:text-muted-foreground tw:tabular-nums"
      >
        {formatTickLabel(0)}
      </span>
    </div>
  )
})
StripTickLabels.displayName = 'StripTickLabels'

interface IStripBarProps {
  session: Session
  dayStartMs: number
  dayEndMs: number
  dayDurationMs: number
  effectiveEndedAt: number
  isHovered: boolean
  onHover: (id: string | null) => void
  onOpen: (session: Session) => void
  projectDateStore?: ProjectDateStore
}

const StripBar: React.FC<IStripBarProps> = React.memo(
  ({
    session,
    dayStartMs,
    dayEndMs,
    dayDurationMs,
    effectiveEndedAt,
    isHovered,
    onHover,
    onOpen,
    projectDateStore,
  }) => {
    const id = String(session.id)
    const startedAt = Number(session.startedAt || 0)
    const endedAtRaw = Number(session.endedAt || 0)
    const isOpenSession = !endedAtRaw
    const isClickable = !!endedAtRaw

    const handleClick = React.useCallback(() => {
      if (isClickable) onOpen(session)
    }, [isClickable, onOpen, session])
    const handleEnter = React.useCallback(() => onHover(id), [onHover, id])
    const handleLeave = React.useCallback(() => onHover(null), [onHover])

    if (!startedAt) return null
    const clampedStart = Math.max(startedAt, dayStartMs)
    const clampedEnd = Math.min(effectiveEndedAt, dayEndMs)
    if (clampedEnd <= clampedStart) return null

    const left = ((clampedStart - dayStartMs) / dayDurationMs) * 100
    const width = ((clampedEnd - clampedStart) / dayDurationMs) * 100

    const ariaLabel = projectDateStore
      ? `${projectDateStore.getTimeToDisplay(startedAt)} – ${
          isOpenSession
            ? Localization.translator.activitySessionOpen
            : projectDateStore.getTimeToDisplay(endedAtRaw)
        }, ${formatDuration(Number(session.durationMs || 0))}`
      : undefined

    return (
      <button
        type="button"
        aria-disabled={!isClickable || undefined}
        onClick={handleClick}
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
        onFocus={handleEnter}
        onBlur={handleLeave}
        aria-label={ariaLabel}
        className={`tw:absolute tw:inset-y-1 tw:rounded-[3px] tw:border-0 tw:bg-primary tw:p-0 tw:transition-all tw:focus-visible:outline-none tw:focus-visible:ring-2 tw:focus-visible:ring-ring tw:focus-visible:ring-offset-1 tw:focus-visible:ring-offset-background ${
          isClickable ? 'tw:cursor-pointer' : 'tw:cursor-default'
        } ${
          isHovered
            ? 'tw:bg-primary tw:shadow-[0_0_0_2px_var(--background),0_0_0_4px_var(--primary)]'
            : 'tw:hover:opacity-90'
        } ${isOpenSession ? 'tw:animate-pulse' : ''}`}
        style={{
          left: `${left}%`,
          width: `max(${width}%, 4px)`,
        }}
      />
    )
  },
)
StripBar.displayName = 'StripBar'

interface IDayActivityStripProps {
  dayDate: Date
  sessions: Session[]
  hoveredSessionId: string | null
  now: number | null
  onHoverSession: (id: string | null) => void
  onOpenSession: (session: Session) => void
  projectDateStore?: ProjectDateStore
}

const DayActivityStrip: React.FC<IDayActivityStripProps> = React.memo(
  ({
    dayDate,
    sessions,
    hoveredSessionId,
    now,
    onHoverSession,
    onOpenSession,
    projectDateStore,
  }) => {
    const dayStartMs = dayDate.getTime()
    const dayEndMs = projectDateStore
      ? projectDateStore.addDays(dayDate, 1).getTime()
      : dayStartMs + MS_PER_DAY
    const dayDurationMs = dayEndMs - dayStartMs

    return (
      <div className="tw:flex tw:flex-col tw:gap-1.5">
        <div className="tw:relative tw:h-9 tw:w-full tw:overflow-hidden tw:rounded-md tw:bg-muted/50">
          <StripGridLines />
          {sessions.map(session => {
            const isOpen = !Number(session.endedAt || 0)
            const effectiveEndedAt = isOpen
              ? (now ?? Number(session.startedAt || 0))
              : Number(session.endedAt || 0)
            return (
              <StripBar
                key={String(session.id)}
                session={session}
                dayStartMs={dayStartMs}
                dayEndMs={dayEndMs}
                dayDurationMs={dayDurationMs}
                effectiveEndedAt={effectiveEndedAt}
                isHovered={hoveredSessionId === String(session.id)}
                onHover={onHoverSession}
                onOpen={onOpenSession}
                projectDateStore={projectDateStore}
              />
            )
          })}
        </div>
        <StripTickLabels />
      </div>
    )
  },
)
DayActivityStrip.displayName = 'DayActivityStrip'

export default DayActivityStrip
